import _ from 'lodash';
import { useRouter } from 'next/router';
import React, { type PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import evaluateExpression from '@/helpers/evaluateExpression';
import getListOrTableField from '@/helpers/getListOrTableField';
import checkFieldValidity from '@/helpers/questionnaires/checkFieldValidity';
import getFieldValue from '@/helpers/questionnaires/getFieldValue';
import trackEvents from '@/helpers/trackEvents';
import useNoodleApi from '@/hooks/useNoodleApi';
import { logError } from '@/providers/ErrorTracking';
import useSentryReplay from '@/providers/ErrorTracking/useSentryReplay';
import logger from '@/providers/Logging';
import * as tsClient from '@/tsClient';
import type { Questionnaire } from '@/typings/Questionnaire';
import type * as QuestionnaireApiModels from '@/typings/questionnaires-api-models';

import useExternalDataSets from '../form/HandleWatchers/HandleAutofills/useExternalDataSets';
import getLinkedListKeys from '../form/helpers/getLinkedListKeys';
import { type BankruptcyExemption } from '../form/List/getDisplayValue';

import { EditingStateProvider } from './EditingStateProvider';
import FormState, { type State } from './FormState';
import HandleQuestionnaireActivity from './HandleQuestionnaireActivity';
import QuestionnaireContext, {
  type FieldResponseMetadata,
  type QuestionnaireContextType,
  type QuestionnairePerson,
} from './QuestionnaireContext';
import QuestionnaireFormProvider from './QuestionnaireFormProvider/QuestionnaireFormProvider';
import QuestionnaireInspector from './QuestionnaireInspector';
import useLoadAgents from './useLoadAiAgents';
import useQuestionnaireLogger from './useQuestionnaireLogger';

type QuestionnaireProviderProps = {
  aiAutofillActions: string[];
  validationRequired?: boolean;
  fieldsByKey: Record<string, Questionnaire.Field>;
  formData: Questionnaire.Questionnaire;
  formRequestId?: string;
  questionnaireResponses: QuestionnaireContextType['questionnaireResponses'];
  onSubmit: (responses: QuestionnaireApiModels.UpdateResponseData, isCompleted?: boolean) => Promise<boolean>;
  userWorkflowId: string | null;
  enabled: boolean;
  initialWorkflowContext?: Record<string, unknown> | null;
  initialPdfFills?: QuestionnaireContextType['pdfFills'];
  initialPdfFillJsonsBySection?: Record<string, Record<string, unknown>>;
  initialDataReferencesBySlug?: QuestionnaireApiModels.DataReference | null;
  pdfTemplates?: QuestionnaireApiModels.PdfTemplate[];
};

const courtDivisionDatasetSlug = 'court-divisions';
const federalExemptionsDatasetSlug = 'federal-bankruptcy-exemptions';
const stateExemptionsDatasetSlug = 'state-exemptions-v0.1';

const QuestionnaireProvider: React.FC<PropsWithChildren<QuestionnaireProviderProps>> = ({
  aiAutofillActions,
  validationRequired,
  fieldsByKey,
  formData,
  formRequestId,
  questionnaireResponses,
  children,
  onSubmit,
  userWorkflowId,
  enabled,
  initialWorkflowContext,
  initialPdfFills,
  initialPdfFillJsonsBySection,
  initialDataReferencesBySlug,
  pdfTemplates,
}) => {
  trackEvents.track('Render QuestionnaireProvider');

  const fieldsByKeyRef = useRef(fieldsByKey);
  fieldsByKeyRef.current = fieldsByKey;
  const formMethods = useForm({ mode: validationRequired ? 'onBlur' : 'onSubmit' });

  const { data: creatorCreditors, getData: getCreatorCreditorsFn } = useNoodleApi(
    tsClient.creators.getCreatorCreditors,
  );
  const { getData: getPersonByIdFn } = useNoodleApi(tsClient.getPersonById);

  const datasets = useExternalDataSets({
    additionalSlugs: [courtDivisionDatasetSlug, federalExemptionsDatasetSlug, stateExemptionsDatasetSlug],
    sections: [],
  });

  const agents = useLoadAgents({
    creatorId: formData.creatorId,
    questionnaireTemplateId: formData.questionnaireTemplateId,
  });
  const [agentsReadyToRun, setAgentsReadyToRun] = useState(false);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    if (agents) {
      timer = setTimeout(() => {
        setAgentsReadyToRun(true);
      }, 5000);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [agents]);

  const fieldsById: Record<string, (typeof fieldsByKey)[keyof typeof fieldsByKey]> = useMemo(() => {
    const partialFieldsById: Record<string, (typeof fieldsByKey)[keyof typeof fieldsByKey]> = {};
    Object.values(fieldsByKey).forEach((field) => {
      partialFieldsById[field.id] = field;
    });
    return partialFieldsById;
  }, [fieldsByKey]);

  const areAllFieldsLoaded = useMemo(() => formData.sections.every((s) => !s.isLoading), [formData.sections]);

  useSentryReplay({ sessionName: `Questionnaire - ${formData.label}` });

  const formValues = useMemo(() => {
    const values: Record<string, unknown> = {};

    if (Object.keys(fieldsById).length === 0) {
      return {};
    }
    const listRowMap = new Map<string, Record<string, unknown>>();
    let reportedMissingFieldTemplates = false;
    const missingFieldTemplates = new Set<string>();
    // First go through all of the responses and create the list+row structure
    (questionnaireResponses ?? []).forEach((response) => {
      const fieldTemplate = fieldsById[response.fieldTemplateId];
      if (!fieldTemplate) {
        if (!reportedMissingFieldTemplates) {
          missingFieldTemplates.add(response.fieldTemplateId);
          reportedMissingFieldTemplates = true;
        }
        return;
      }
      // Check if this is a list/table field itself (represents a row)
      if (fieldTemplate.type === 'list' || fieldTemplate.type === 'table') {
        const thisRow = {
          _associatedWithId: response.associatedWithId,
          _existsInDb: true,
          _id: response.id,
          _isRemoved: response.isRemoved,
        };
        listRowMap.set(response.id, thisRow);
        if (!Array.isArray(values[fieldTemplate.key])) {
          values[fieldTemplate.key] = [];
        }
        const thisList = values[fieldTemplate.key] as unknown[];
        thisList[response.index ?? 0] = thisRow;
        values[fieldTemplate.key] = thisList;
      }
    });

    (questionnaireResponses ?? []).forEach((response) => {
      const fieldTemplate = fieldsById[response.fieldTemplateId];
      if (!fieldTemplate) {
        // error logged above, no need to log again
        return;
      }

      if (fieldTemplate.type === 'list' || fieldTemplate.type === 'table') {
        return;
      }

      // Check if this field belongs to a list/table (is a cell)
      const listOrTableParent = getListOrTableField({
        fieldId: response.fieldTemplateId,
        fieldsById,
      });

      if (response.listRowResponseId && listOrTableParent) {
        const listOrTableRow = listRowMap.get(response.listRowResponseId);
        const rowResponse = questionnaireResponses.find((r) => r.id === response.listRowResponseId);
        // This means that there is a cell that is not deleted, but whose row is.
        // This is a bug, ignore those cases.
        if (!rowResponse) {
          // @todo - log somehow
          return;
        }
        if (!listOrTableRow) {
          missingFieldTemplates.add(response.fieldTemplateId);
          reportedMissingFieldTemplates = true;
          return;
        }
        listOrTableRow[fieldTemplate.key] = response.value;
      } else {
        values[fieldTemplate.key] = response.value;
      }
    });

    Object.values(fieldsById).forEach((field) => {
      if (field.type === 'list') {
        const uniqueLinkedFields = Array.from(
          new Set(field?.linkedFields.map((linkedField) => linkedField.linkedFieldTemplateId)),
        );
        if (uniqueLinkedFields.length > 1) {
          logger.error('Multiple linked fields in list', { field, flow: 'questionnaire' });
        }
        const originalListKey = uniqueLinkedFields[0];
        if (originalListKey) {
          const originalList = values[originalListKey];
          if (originalList) {
            values[field.key] = _.cloneDeep(originalList);
          }
        }
      }
    });

    if (missingFieldTemplates.size > 0 && areAllFieldsLoaded) {
      logError(new Error('List/table row not found for response'), {
        numberOfMissingFieldTemplates: missingFieldTemplates.size,
        questionnaireId: formData.id,
      });
    }

    return values;
  }, [formData.id, fieldsById, questionnaireResponses, areAllFieldsLoaded]);

  // Sync RHF state whenever the computed form values change. This must be a useEffect
  // rather than a call inside the useMemo above — calling formMethods.reset() during
  // render triggers RHF state updates on all Controller components mid-render, which
  // causes the React "setState while rendering" warning and makes all validators fire
  // simultaneously (the section-count jump bug).
  useEffect(() => {
    formMethods.reset(formValues, { keepErrors: true });
  }, [formMethods, formValues]);

  useEffect(() => {
    if (formData.creatorId) {
      getCreatorCreditorsFn({
        creatorId: formData.creatorId,
        page: 1,
        perPage: 100_000,
      });
    }
  }, [formData.creatorId, getCreatorCreditorsFn]);

  const refetchCreditors = useCallback((): void => {
    if (formData.creatorId) {
      getCreatorCreditorsFn({
        creatorId: formData.creatorId,
        page: 1,
        perPage: 100_000,
      });
    }
  }, [formData.creatorId, getCreatorCreditorsFn]);

  // Register all field keys (including hidden/conditional ones) so that formMethods.getValues()
  // returns them during submit. Validation rules are owned by FormField via getValidationRules.
  useEffect(() => {
    formData.sections.forEach((section) => {
      section.fields.forEach((field) => {
        formMethods.register(field.key);
      });
    });
  }, [formMethods, formData.sections]);

  const router = useRouter();

  const [currentSection, setCurrentSectionState] = useState(
    formData.sections.length > 0 ? formData.sections[0].id : '',
  );

  // Initialize currentSection from path slug; re-runs if the slug or section list changes (e.g. template swap)
  useEffect(() => {
    if (!router.isReady) return;
    const slugFromUrl = Array.isArray(router.query.slug) ? router.query.slug[0] : router.query.slug;
    if (!slugFromUrl) return;
    const match = formData.sections.find((s) => s.slug === slugFromUrl);
    if (match && match.id !== currentSection) {
      setCurrentSectionState(match.id);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps -- currentSection intentionally omitted: should only fire on URL changes (external nav), not on programmatic setCurrentSection calls which would read a stale URL and revert.
  }, [router.isReady, router.query.slug, formData.sections]);

  const [isEnabled, setIsEnabled] = useState(enabled);
  const [isSubmittable, setIsSubmittable] = useState(false);
  const [newListItemId, setNewListItemId] = useState<string | null>(null);
  const [sectionErrors, setSectionErrors] = useState<{ [key: string]: number }>({});
  const [referenceData, setReferenceData] = useState<QuestionnaireApiModels.DataReference[]>([]);
  const [activeUsers, setActiveUsers] = useState<QuestionnaireContextType['activeUsers']>([]);
  const [questionnairePeople, setQuestionnairePeople] = useState<Record<string, QuestionnairePerson>>({});

  const [sectionVisibility, setSectionVisibilityState] = useState<Record<string, boolean>>({});
  const setSectionVisibility = useCallback((sectionId: string, isVisible: boolean): void => {
    setSectionVisibilityState((prevState) =>
      prevState[sectionId] !== isVisible
        ? {
            ...prevState,
            [sectionId]: isVisible,
          }
        : prevState,
    );
  }, []);
  type PdfFillWithExtras = QuestionnaireApiModels.PdfFill & {
    expression: Omit<QuestionnaireApiModels.Expression, 'variables'> | null;
    pdfFieldTemplate: QuestionnaireApiModels.PdfFieldTemplate & {
      pdfTemplate: QuestionnaireApiModels.PdfTemplate;
    };
    listFieldTemplate: Pick<QuestionnaireApiModels.FieldTemplate, 'id' | 'listFilterExpression'> | null;
  };

  const [pdfFills, setPdfFills] = useState<PdfFillWithExtras[]>(initialPdfFills ?? []);
  const [userWorkflowContext, setUserWorkflowContext] = useState<Record<string, unknown>>(initialWorkflowContext ?? {});
  const [pdfFillJsonsBySection, setPdfFillJsonsBySection] = useState<Record<string, Record<string, unknown>>>(
    initialPdfFillJsonsBySection ?? {},
  );
  const [dataReferencesBySlug, setDataReferencesBySlug] = useState<QuestionnaireApiModels.DataReference | null>(
    initialDataReferencesBySlug ?? null,
  );
  const [isSavingResponse, setIsSavingResponse] = useState<boolean>(false);

  // Sync state when parent props change (data fetched later in QuestionnaireDetails)
  useEffect(() => {
    if (initialPdfFills) setPdfFills(initialPdfFills);
  }, [initialPdfFills]);
  useEffect(() => {
    if (initialWorkflowContext) setUserWorkflowContext(initialWorkflowContext);
  }, [initialWorkflowContext]);
  useEffect(() => {
    if (initialPdfFillJsonsBySection) setPdfFillJsonsBySection(initialPdfFillJsonsBySection);
  }, [initialPdfFillJsonsBySection]);
  useEffect(() => {
    if (initialDataReferencesBySlug !== undefined) setDataReferencesBySlug(initialDataReferencesBySlug ?? null);
  }, [initialDataReferencesBySlug]);

  // Build a comprehensive field template map for all fields (by id), used by PDF fill generator
  const fieldTemplatesMap = useMemo(() => {
    const allFields = Object.values(fieldsByKey);
    return new Map(
      allFields.map((field) => [
        field.id,
        {
          conditionExpression: field.conditionExpression ?? null,
          id: field.id,
          key: field.key,
          listFieldTemplateId: field.listFieldTemplateId,
          options: field.options ?? [],
          tableColumns: field.type === 'table' ? field.tableColumns : [],
          type: field.type,
        },
      ]),
    );
  }, [fieldsByKey]);

  const getSectionProgress = useCallback(
    (sectionId: string, fieldsVisibility: Record<string, boolean>): number => {
      const section = formData.sections.find((_section) => _section.id === sectionId);
      if (!section) return 0;

      let totalRequiredFields = section.fields
        .filter((field) => !field.listFieldTemplateId && fieldsVisibility[field.key])
        .filter((field) => field.isRequired && field.type !== 'explanation').length;

      let completedRequiredFields = 0;

      section.fields
        .filter((field) => !field.listFieldTemplateId && fieldsVisibility[field.key])
        .forEach((field) => {
          if (field.isRequired) {
            if (field.type === 'list') {
              const fieldValue = (formMethods.getValues()[field.key] || []) as Array<Record<string, unknown>>;
              if (fieldValue.length > 0) {
                completedRequiredFields += 1;
              }
              fieldValue.forEach((listItemValue) => {
                if (!listItemValue._associatedWithId) {
                  field.listItemTemplates?.forEach((subField) => {
                    let isVisible = true;

                    if (subField.conditionExpression && subField.conditionExpression.expression) {
                      const variablesWithValues =
                        subField.conditionExpression.variables?.map((variable) => ({
                          ...variable,
                          value: listItemValue[variable.referenceId],
                        })) || [];

                      const result = evaluateExpression({
                        expression: subField.conditionExpression.expression,
                        id: 'getSectionProgress1',
                        variables: variablesWithValues.reduce(
                          (acc, val) => ({
                            ...acc,
                            [val.variableName]: val.value,
                          }),
                          {},
                        ),
                      });

                      isVisible = Boolean(result);
                    }

                    if (isVisible && subField.isRequired) {
                      totalRequiredFields += 1;
                      const subFieldValue = listItemValue[subField.key];

                      const isFieldValid = checkFieldValidity(subField, subFieldValue);

                      if (isFieldValid) {
                        completedRequiredFields += 1;
                      }
                    }
                  });
                }
              });
            } else if (field.type === 'table') {
              // table is always "complete"
              completedRequiredFields += 1;

              const fieldValue = (formMethods.getValues()[field.key] || []) as Array<Record<string, unknown>>;

              field.tableColumns.forEach((column, idx) => {
                const { conditionExpression } = column;
                let isVisible;
                const conditionVariables = formMethods.watch(
                  _.uniq(
                    field.tableColumns
                      ?.map((c) => c.conditionExpression?.variables?.map((v) => v.referenceId) || [])
                      .reduce((acc, val) => [...acc, ...val]),
                  ),
                );
                if (conditionExpression?.expression) {
                  const variablesWithValues =
                    conditionExpression.variables?.map((variable, varIdx) => ({
                      ...variable,
                      value: getFieldValue({
                        field: fieldsByKeyRef.current[variable.referenceId],
                        value: conditionVariables[varIdx],
                      }),
                    })) || [];
                  const result = evaluateExpression({
                    expression: conditionExpression.expression,
                    id: 'getSectionProgress2',
                    variables: variablesWithValues.reduce(
                      (acc, val) => ({
                        ...acc,
                        [val.variableName]: val.value,
                      }),
                      {},
                    ),
                  });
                  isVisible = Boolean(result);
                } else {
                  isVisible = true;
                }
                if (isVisible) {
                  field?.listItemTemplates?.forEach((subField) => {
                    if (fieldsVisibility[subField.key] && subField.isRequired) {
                      totalRequiredFields += 1;
                      const subFieldValue = fieldValue[idx]?.[subField.key];

                      const isFieldValid = checkFieldValidity(subField, subFieldValue);

                      if (isFieldValid) {
                        completedRequiredFields += 1;
                      }
                    }
                  });
                }
              });
            } else if (field.type === 'median-income' || field.type === 'monthly-income') {
              // median and monthly income are always "complete"
              completedRequiredFields += 1;
            } else {
              const fieldKey = field.key;
              const fieldValue = formMethods.getValues()[fieldKey];

              const isFieldValid = checkFieldValidity(field, fieldValue);

              if (isFieldValid) {
                completedRequiredFields += 1;
              }
            }
          }
        });

      return totalRequiredFields === 0 ? 100 : (completedRequiredFields / totalRequiredFields) * 100;
    },
    [formData, formMethods],
  );

  const getSubsectionProgress = useCallback(
    (
      sectionId: string,
      subsection: string,
      fieldsVisibility: Record<string, boolean>,
    ): {
      completedFields: number;
      subsectionProgressPercentage: number | null;
      totalFields: number;
    } | null => {
      const section = formData.sections.find((_section) => _section.id === sectionId);
      if (!section) return null;

      const fieldsInSubsectionKeys = section.fields
        .filter(
          (field) =>
            field.subsection === subsection &&
            fieldsVisibility[field.key] &&
            field.type !== 'explanation' &&
            field.isRequired,
        )
        .map((field) => field.key);

      const subsectionFieldsVisibility = Object.fromEntries(
        Object.entries(fieldsVisibility).filter(([key]) => fieldsInSubsectionKeys.includes(key)),
      );

      // reuse getSectionProgress by passing the fields in the subsection
      const subsectionProgressPercentage = fieldsInSubsectionKeys.length
        ? getSectionProgress(sectionId, subsectionFieldsVisibility)
        : null;

      // calculate completedFields and totalFields based on the percentage
      const totalFields = fieldsInSubsectionKeys.length;
      const completedFields = subsectionProgressPercentage
        ? Math.round((subsectionProgressPercentage / 100) * totalFields)
        : 0;

      return {
        completedFields,
        subsectionProgressPercentage,
        totalFields,
      };
    },
    [formData, getSectionProgress],
  );

  const handleChangeFormState = useCallback((state: State) => {
    setIsSubmittable(state === 'waiting');
  }, []);

  const courtDivisionsDataset = useMemo(
    () =>
      (datasets[courtDivisionDatasetSlug] as {
        table: Array<{ key: string; label: string; stateOfFilingFilter: string }>;
      } | null) ?? null,
    [datasets],
  );

  const stateExemptionsDataset = useMemo(
    () => (datasets[stateExemptionsDatasetSlug] as { table: BankruptcyExemption[] } | null) ?? null,
    [datasets],
  );

  const federalExemptionsDataset = useMemo(
    () => (datasets[federalExemptionsDatasetSlug] as { table: BankruptcyExemption[] } | null) ?? null,
    [datasets],
  );

  // Initialize questionnaire logger early
  const questionnaireLogger = useQuestionnaireLogger(formData.id);

  // Enhanced setCurrentSection with logging and URL sync
  const setCurrentSection = useCallback(
    (sectionId: string) => {
      const previousSection = currentSection;
      const newSection = formData.sections.find((s) => s.id === sectionId);
      const previousSectionData = formData.sections.find((s) => s.id === previousSection);

      if (previousSection !== sectionId) {
        questionnaireLogger.info('User navigated to section', {
          fromSectionId: previousSection,
          fromSectionLabel: previousSectionData?.label,
          toSectionId: sectionId,
          toSectionLabel: newSection?.label,
        });
      }

      setCurrentSectionState(sectionId);

      if (newSection?.slug) {
        void router.replace(
          {
            pathname: router.pathname,
            query: { ...router.query, slug: [newSection.slug] },
          },
          undefined,
          { shallow: true },
        );
      }
    },
    [currentSection, formData.sections, questionnaireLogger, router],
  );

  // Build field response metadata mapping (now with state for real-time updates)
  const [fieldResponseMapping, setFieldResponseMapping] = useState<Record<string, FieldResponseMetadata>>({});

  // Log questionnaire initialization
  useEffect(() => {
    questionnaireLogger.info('Questionnaire initialized', {
      fieldCount: Object.keys(fieldsByKey).length,
      responseCount: questionnaireResponses?.length || 0,
      sectionCount: formData.sections.length,
    });
  }, [formData.id]); // Only log once per questionnaire

  // Build initial mapping from questionnaireResponses
  useEffect(() => {
    const mapping: Record<string, FieldResponseMetadata> = {};

    (questionnaireResponses ?? []).forEach((response) => {
      const fieldTemplate = fieldsById[response.fieldTemplateId];
      if (!fieldTemplate) {
        return;
      }

      if (fieldTemplate.type === 'list' || fieldTemplate.type === 'table') {
        const linkedKeys = getLinkedListKeys({ fieldTemplate, fieldsById });
        linkedKeys.forEach((linkedKey) => {
          const cellResponses = questionnaireResponses.filter((r) => r.listRowResponseId === response.id);
          cellResponses.forEach((cellResponse) => {
            const cellFieldTemplate = fieldsById[cellResponse.fieldTemplateId];
            if (cellFieldTemplate) {
              const fullFieldKey = `${linkedKey}[${response.index ?? 0}].${cellFieldTemplate.key}`;
              mapping[fullFieldKey] = {
                submittedById: cellResponse.submittedById,
                updatedAt: cellResponse.updatedAt,
              };
            }
          });
        });
      } else {
        const listOrTableParent = getListOrTableField({
          fieldId: fieldTemplate.id,
          fieldsById,
        });
        if (!listOrTableParent) {
          mapping[fieldTemplate.key] = {
            submittedById: response.submittedById,
            updatedAt: response.updatedAt,
          };
        }
      }
    });

    setFieldResponseMapping(mapping);
  }, [questionnaireResponses, fieldsById]);

  // Function to update field response metadata in real-time
  const updateFieldResponseMetadata = useCallback((fieldKey: string, metadata: FieldResponseMetadata) => {
    setFieldResponseMapping((prev) => ({
      ...prev,
      [fieldKey]: metadata,
    }));
  }, []);

  // Build initial questionnairePeople from unique submittedByIds
  const fetchingPersonIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const uniqueSubmittedByIds = new Set<string>();

    (questionnaireResponses ?? []).forEach((response) => {
      if (response.submittedById) {
        uniqueSubmittedByIds.add(response.submittedById);
      }
    });

    // Filter out people we already have or are currently fetching
    const personIdsToFetch = Array.from(uniqueSubmittedByIds).filter(
      (personId) => !questionnairePeople[personId] && !fetchingPersonIdsRef.current.has(personId),
    );

    if (personIdsToFetch.length === 0) {
      return;
    }

    // Mark these IDs as being fetched to prevent duplicate requests
    personIdsToFetch.forEach((personId) => {
      fetchingPersonIdsRef.current.add(personId);
    });

    // Fetch person data for each unique submittedById that we don't already have
    const fetchPersonPromises = personIdsToFetch.map(async (personId) => {
      try {
        const response = await getPersonByIdFn({ id: personId });
        if (response.data) {
          setQuestionnairePeople((prev) => ({
            ...prev,
            [personId]: {
              color: response.data!.primaryColour?.hex || null,
              id: response.data!.id,
              name: response.data!.name,
              url: response.data!.profilePhoto?.url || null,
            },
          }));
        }
      } catch (error) {
        logger.error('Failed to fetch person:', { error: error?.toString(), flow: 'questionnaire', personId });
      } finally {
        // Remove from fetching set when done (success or error)
        fetchingPersonIdsRef.current.delete(personId);
      }
    });

    // Wait for all person fetches to complete
    Promise.all(fetchPersonPromises).catch((error) => {
      logger.error('Error fetching questionnaire people:', { error: error?.toString(), flow: 'questionnaire' });
    });
  }, [questionnaireResponses, getPersonByIdFn]);

  // Function to get field response metadata
  const getFieldResponseMetadata = useCallback(
    (fieldKey: string): FieldResponseMetadata | null => fieldResponseMapping[fieldKey] || null,
    [fieldResponseMapping],
  );

  // Function to get questionnaire person (with caching)
  const getQuestionnairePerson = useCallback(
    async (personId: string): Promise<QuestionnairePerson | null> => {
      // Check if we already have this person
      if (questionnairePeople[personId]) {
        return questionnairePeople[personId];
      }

      // Fetch the person if we don't have them
      try {
        const response = await getPersonByIdFn({ id: personId });
        if (response.data) {
          const person: QuestionnairePerson = {
            color: response.data.primaryColour?.hex || null,
            id: response.data.id,
            name: response.data.name,
            url: response.data.profilePhoto?.url || null,
          };

          // Cache the person
          setQuestionnairePeople((prev) => ({
            ...prev,
            [personId]: person,
          }));

          return person;
        }
      } catch (error) {
        logger.error('Failed to fetch person:', { error: error?.toString(), flow: 'questionnaire', personId });
      }

      return null;
    },
    [questionnairePeople, getPersonByIdFn],
  );

  const values = useMemo(() => {
    trackEvents.track('QuestionnaireContext - valuesMemoized');
    const newContextValues: QuestionnaireContextType = {
      activeUsers,
      agents,
      agentsReadyToRun,
      aiAutofillActions,
      checkFieldValidity,
      courtDivisionsDataset,
      creditors: creatorCreditors?.items ?? [],
      currentSection,
      dataReferencesBySlug,
      defaultValues: formValues,
      federalExemptionsDataset,
      fieldTemplatesMap,
      fieldsById,
      fieldsByKey,
      formData,
      formRequestId: formRequestId ?? null,
      getFieldResponseMetadata,
      getQuestionnairePerson,
      getSectionProgress,
      getSubsectionProgress,
      isEnabled,
      isSavingResponse,
      isSubmittable,
      logger: questionnaireLogger,
      newListItemId,
      onSubmit,
      pdfFillJsonsBySection,
      pdfFills,
      pdfTemplates: pdfTemplates ?? [],
      questionnaireId: formData.id,
      questionnairePeople,
      questionnaireResponses,
      questionnaireTemplateId: formData.questionnaireTemplateId,
      referenceData,
      refetchCreditors,
      sectionErrors,
      sectionVisibility,
      setActiveUsers,
      setCurrentSection,
      setIsEnabled,
      setIsSavingResponse,
      setIsSubmittable,
      setNewListItemId,
      setPdfFills,
      setReferenceData,
      setSectionErrors,
      setSectionVisibility,
      setUserWorkflowContext,
      stateExemptionsDataset,
      updateFieldResponseMetadata,
      userWorkflowContext,
      userWorkflowId,
    };
    return newContextValues;
  }, [
    activeUsers,
    setActiveUsers,
    agents,
    agentsReadyToRun,
    aiAutofillActions,
    courtDivisionsDataset,
    creatorCreditors,
    currentSection,
    federalExemptionsDataset,
    fieldsById,
    fieldsByKey,
    formData,
    formValues,
    getSectionProgress,
    getSubsectionProgress,
    onSubmit,
    questionnaireResponses,
    referenceData,
    refetchCreditors,
    sectionErrors,
    sectionVisibility,
    setCurrentSection,
    setReferenceData,
    setSectionErrors,
    setSectionVisibility,
    stateExemptionsDataset,
    userWorkflowId,
    isEnabled,
    setIsEnabled,
    isSubmittable,
    setIsSubmittable,
    newListItemId,
    setNewListItemId,
    pdfFills,
    setPdfFills,
    pdfTemplates,
    userWorkflowContext,
    setUserWorkflowContext,
    getFieldResponseMetadata,
    updateFieldResponseMetadata,
    questionnairePeople,
    getQuestionnairePerson,
    questionnaireLogger,
  ]);

  return (
    <QuestionnaireContext.Provider value={values}>
      <EditingStateProvider>
        <QuestionnaireFormProvider validationRequired={validationRequired} formMethods={formMethods} id='Global'>
          <QuestionnaireInspector fieldsByKey={fieldsByKey} formMethods={formMethods} sections={formData.sections} />
          <HandleQuestionnaireActivity
            currentSection={currentSection}
            onChangeActiveUsers={setActiveUsers}
            questionnaireId={formData.id}
          />
          <FormState onChange={handleChangeFormState} />
          {children}
        </QuestionnaireFormProvider>
      </EditingStateProvider>
    </QuestionnaireContext.Provider>
  );
};

export default QuestionnaireProvider;
